import { FuelPlanFuelStop, FuelPlanStop } from "types";

export type SelectedFuelStops = Record<number, FuelPlanFuelStop>;

export type FuelPlanDisplayLeg = {
  fromDisplayName: string;
  toDisplayName: string;
  miles: number;
};

const isFuelingStop = (stop: FuelPlanStop): boolean =>
  stop.guidance?.code === "FillUp" ||
  stop.guidance?.code === "ChooseNetworkFuelStop" ||
  stop.guidance?.code === "JustEnough";

export const getInitialSelectedFuelStops = (
  stops: FuelPlanStop[]
): SelectedFuelStops => {
  return stops.reduce<SelectedFuelStops>((selected, stop) => {
    const defaultFuelStop = stop.fuelStops.find(
      (fuelStop) => fuelStop.isDefault
    );
    if (defaultFuelStop) {
      selected[stop.sequence] = defaultFuelStop;
    }

    return selected;
  }, {});
};

export const getStopDisplayName = (
  stop: FuelPlanFuelStop | undefined
): string => stop?.name ?? "";

export const getFuelStopLocation = (
  stop: FuelPlanFuelStop | undefined
): string => (stop ? [stop.city, stop.state].filter(Boolean).join(", ") : "");

export const formatMiles = (miles: number | null | undefined): string =>
  Number.isFinite(miles) ? `${miles} mi` : "-- mi";

export const getCalculatorFuelingLabel = (
  calculatorStop: FuelPlanStop | null,
  selectedFuelStops: SelectedFuelStops
): string => {
  if (!calculatorStop) return "N/A";

  const selectedFuelStop = selectedFuelStops[calculatorStop.sequence];
  if (selectedFuelStop) return getStopDisplayName(selectedFuelStop);

  return (
    [calculatorStop.city, calculatorStop.state].filter(Boolean).join(", ") ||
    calculatorStop.displayName
  );
};

export const getPlannedFuelStops = (
  stops: FuelPlanStop[],
  selectedFuelStops: SelectedFuelStops
): FuelPlanFuelStop[] =>
  stops
    .map((stop) => selectedFuelStops[stop.sequence])
    .filter((stop): stop is FuelPlanFuelStop => Boolean(stop));

export const getDisplayLeg = (
  stop: FuelPlanStop,
  stops: FuelPlanStop[],
  selectedFuelStops: SelectedFuelStops
): FuelPlanDisplayLeg | null => {
  if (!stop.nextLeg) return null;

  const nextStop = stops.find((item) => item.sequence === stop.sequence + 1);
  const toSelectedFuelStop = nextStop
    ? selectedFuelStops[nextStop.sequence]
    : undefined;
  if (toSelectedFuelStop) {
    return {
      fromDisplayName:
        getStopDisplayName(selectedFuelStops[stop.sequence]) ||
        stop.displayName,
      toDisplayName:
        getStopDisplayName(toSelectedFuelStop) ||
        nextStop?.displayName ||
        "Stop",
      miles: toSelectedFuelStop.milesFromPreviousStop ?? stop.nextLeg.miles,
    };
  }

  const fromSelectedFuelStop = selectedFuelStops[stop.sequence];
  if (fromSelectedFuelStop) {
    return {
      fromDisplayName:
        getStopDisplayName(fromSelectedFuelStop) || stop.displayName,
      toDisplayName: nextStop?.displayName || "Stop",
      miles: fromSelectedFuelStop.milesToNextStop ?? stop.nextLeg.miles,
    };
  }

  return {
    fromDisplayName: stop.displayName,
    toDisplayName: nextStop?.displayName || "Stop",
    miles: stop.nextLeg.miles,
  };
};

export const getCalculatorLeg = (
  stop: FuelPlanStop,
  stops: FuelPlanStop[],
  selectedFuelStops: SelectedFuelStops
): FuelPlanDisplayLeg | null => {
  const displayedLeg = getDisplayLeg(stop, stops, selectedFuelStops);
  if (!displayedLeg || !stop.nextLeg) return null;

  const stopIndex = stops.findIndex((item) => item.sequence === stop.sequence);
  const nextFuelStop = stops
    .slice(stopIndex + 1)
    .find(isFuelingStop);
  const selectedNextFuelStop = nextFuelStop
    ? selectedFuelStops[nextFuelStop.sequence]
    : undefined;
  const selectedCurrentFuelStop = selectedFuelStops[stop.sequence];

  return {
    fromDisplayName: displayedLeg.fromDisplayName,
    toDisplayName:
      getStopDisplayName(selectedNextFuelStop) ||
      nextFuelStop?.displayName ||
      displayedLeg.toDisplayName,
    miles:
      selectedCurrentFuelStop?.milesToNextFuelStop ??
      stop.nextLeg.milesToNextFuelStop ??
      displayedLeg.miles,
  };
};

export const getDefaultCalculatorStop = (
  stops: FuelPlanStop[]
): FuelPlanStop | undefined =>
  stops.find((stop) => stop.nextLeg?.isCalculatorDefault) ??
  stops.find((stop) => stop.nextLeg?.isCalculatorEligible);

export const getCalculatorStopForFuelSelection = (
  stops: FuelPlanStop[],
  sequence?: number,
  fuelStopId?: string
): FuelPlanStop | undefined => {
  const selectedRecommendation =
    sequence && fuelStopId
      ? stops.find(
          (stop) =>
            stop.sequence === sequence &&
            stop.nextLeg?.isCalculatorEligible &&
            stop.fuelStops.some(
              (fuelStop) =>
                fuelStop.isDefault && String(fuelStop.id) === String(fuelStopId)
            )
        )
      : undefined;

  return selectedRecommendation ?? getDefaultCalculatorStop(stops);
};
